/* eslint-disable react/no-multi-comp */
import React, { FC, useMemo } from 'react';
import { InvoiceV2ColumnInfo, InvoiceV2ListItem } from '../invoices.model';
import { getAmountWithCurrency } from 'utils/amountUtils';
import DateUtils from 'utils/dateUtils';
import { GenericTable } from 'components';
import { getStringFiller } from 'utils/stringUtils';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { useAppSelector } from 'hooks';
import { TableColumn } from 'components/generic-table/types';
import { useNavigate, useParams } from 'react-router-dom';
import CustomAvatar from 'components/avatar/CustomAvatar';
import { getInitials } from 'utils/commonUtils';
import { InvoiceStatus } from '../invoices.model';
import { getDisputeRefundStatusText, getInvoiceStatus, getInvoiceStatusColor } from '../utils';
import { useGetMerchantCustomizationsQuery } from 'containers/settings/api';
import { PaymentCategoryTypes } from 'containers/settings/type';
import PaymentMethodsCell from './PaymentMethodsCell';
import { getJobStatusColor, getJobStatusText } from 'containers/jobs/utils';
import { JobStatus } from 'containers/jobs/constant';
import routesPath from 'routes/RoutesPath';
import InvoiceRowActions from './InvoiceRowActions';
import { getEstimateStatusColor, getEstimateStatusText } from 'containers/estimates/utils';
import { EstimateStatus } from 'containers/estimates/constants';
import { DISPUTE_REFUND_STATUS_INDICATOR } from '../constants';

interface InvoicesTableV2Props {
  invoicesList: InvoiceV2ListItem[];
  onInvoiceClick: (invoiceId: string) => void;
  columnInfo?: InvoiceV2ColumnInfo[];
  onSortChange?: (sortBy: string, sortOrder: string) => void;
  currentSortBy?: string;
  currentSortOrder?: string;
  stickyHorizontalScrollbar?: { bottomOffsetPx: number };
}

const InvoicesTableV2: FC<InvoicesTableV2Props> = ({
  invoicesList,
  onInvoiceClick,
  columnInfo,
  onSortChange,
  currentSortBy = 'createdAt',
  currentSortOrder = 'DESC',
  stickyHorizontalScrollbar
}) => {
  const currentTeam = useAppSelector(selectCurrentTeam);
  const { rootInvoiceId: invoiceIdFromUrl } = useParams();

  const navigate = useNavigate();

  const { data: merchantCustomisations } = useGetMerchantCustomizationsQuery({
    teamId: currentTeam.id.toString()
  });

  const { paymentModes } = merchantCustomisations || {};
  const isMarkAsPaidEnabled = paymentModes?.some(
    item => item.category.type === PaymentCategoryTypes.MANUAL && item.enabled
  );

  const formatDate = (date: string) => {
    if (!date) {
      return '-';
    }
    const dateObj = new Date(date);
    const currentYear = new Date().getFullYear();
    const dateYear = dateObj.getFullYear();

    if (dateYear === currentYear) {
      return DateUtils.getDateInFormat({
        date: dateObj,
        dateFormat: 'MMM dd'
      });
    }
    return DateUtils.getDateInFormat({
      date: dateObj,
      dateFormat: 'MMM dd, yyyy'
    });
  };

  const formatInvoiceNumber = (invoiceNumber: string) => {
    if (!invoiceNumber) return '-';
    return invoiceNumber.replace(/^IN-/, '');
  };

  const getStatusBadge = ({
    status,
    dueDate,
    paidAt,
    refundedAt,
    closedAt
  }: {
    status: string;
    dueDate: string;
    paidAt: string;
    refundedAt: string;
    closedAt: string;
  }) => {
    const statusColor = getInvoiceStatusColor(status as InvoiceStatus);
    const statusText = getInvoiceStatus(status as InvoiceStatus);
    const today = DateUtils.getNow();
    const differenceInDueDate = Math.abs(
      DateUtils.getDifferenceInDays({
        primaryDate: dueDate ? DateUtils.parseISODateString(dueDate) : today,
        secondaryDate: today
      })
    );
    const differenceInPaidDate = Math.abs(
      DateUtils.getDifferenceInDays({
        primaryDate: paidAt ? DateUtils.parseISODateString(paidAt) : today,
        secondaryDate: today
      })
    );
    const differenceInRefundedDate = Math.abs(
      DateUtils.getDifferenceInDays({
        primaryDate: refundedAt ? DateUtils.parseISODateString(refundedAt) : today,
        secondaryDate: today
      })
    );
    const differenceInClosedDate = Math.abs(
      DateUtils.getDifferenceInDays({
        primaryDate: closedAt ? DateUtils.parseISODateString(closedAt) : today,
        secondaryDate: today
      })
    );
    let description = '';
    if (status === InvoiceStatus.PENDING) {
      description =
        differenceInDueDate === 0
          ? 'Due today'
          : `Due in ${differenceInDueDate} day${differenceInDueDate === 1 ? '' : 's'}`;
    } else if (status === InvoiceStatus.PARTIALLY_PAID) {
      description =
        differenceInDueDate === 0
          ? 'Total due today'
          : `Total due in ${differenceInDueDate} day${differenceInDueDate === 1 ? '' : 's'}`;
    } else if (status === InvoiceStatus.PAID || status === InvoiceStatus.PAID_QR) {
      description =
        differenceInPaidDate === 0
          ? 'Today'
          : `${differenceInPaidDate} day${differenceInPaidDate === 1 ? '' : 's'} ago`;
    } else if (status === InvoiceStatus.OVERDUE) {
      description =
        differenceInDueDate === 0 ? 'Today' : `Since ${differenceInDueDate} day${differenceInDueDate === 1 ? '' : 's'}`;
    } else if (status === InvoiceStatus.REFUNDED || status === InvoiceStatus.PARTIALLY_REFUNDED) {
      description =
        differenceInRefundedDate === 0
          ? 'Today'
          : `${differenceInRefundedDate} day${differenceInRefundedDate === 1 ? '' : 's'} ago`;
    } else if (status === InvoiceStatus.CLOSED_AMOUNT_ADJUSTED || status === InvoiceStatus.CLOSED_BAD_DEBT) {
      description =
        differenceInClosedDate === 0
          ? 'Today'
          : `${differenceInClosedDate} day${differenceInClosedDate === 1 ? '' : 's'} ago`;
    }

    return (
      <div className="flex flex-col gap-1">
        <div className="text-sbase font-bold" style={{ color: statusColor }}>
          {statusText}
        </div>
        {description && <div className="text-px13 font-normal text-primaryText">{description}</div>}
      </div>
    );
  };

  const getDisputeRefundStatusBadge = (statusIndicator: DISPUTE_REFUND_STATUS_INDICATOR) => {
    if (!statusIndicator) return '-';

    return (
      <div className="text-sbase font-semibold text-tertiaryText">{getDisputeRefundStatusText(statusIndicator)}</div>
    );
  };

  const getEstimateStatusBadge = (estimateStatus: string) => {
    if (!estimateStatus) return '-';
    return (
      <div
        className="text-sbase font-semibold capitalize"
        style={{ color: getEstimateStatusColor(estimateStatus as EstimateStatus) }}>
        {getEstimateStatusText(estimateStatus as EstimateStatus)}
      </div>
    );
  };

  const getJobStatusBadge = (jobStatus: string) => {
    if (!jobStatus) return '-';
    const jobStatusEnumValue = jobStatus as JobStatus;
    return (
      <div
        className="text-sbase font-semibold text-primaryText"
        style={{ color: getJobStatusColor(jobStatusEnumValue) }}>
        {getJobStatusText(jobStatusEnumValue)}
      </div>
    );
  };

  const handleJobClick = (jobId: string) => {
    navigate(routesPath.JOB_BY_ID.replace(':teamId', currentTeam.id.toString()).replace(':rootJobId', jobId));
  };

  const columns = useMemo<TableColumn<InvoiceV2ListItem>[]>(() => {
    const baseColumns: TableColumn<InvoiceV2ListItem>[] = [
      {
        id: 'createdAt',
        header: 'Issued date',
        accessor: invoice => (
          <div className="truncate text-sbase text-primaryText">{formatDate(invoice.createdAt)}</div>
        ),
        width: 'min-w-[120px] w-[120px]',
        sortable: true,
        fixed: 'left'
      },
      {
        id: 'invoiceNumber',
        header: 'Invoice No.',
        accessor: invoice => (
          <div
            className="break-words text-sbase font-semibold text-primaryText line-clamp-1"
            title={invoice.invoiceNumber}>
            {formatInvoiceNumber(invoice.invoiceNumber)}
          </div>
        ),
        width: 'min-w-[120px] w-[120px]',
        fixed: 'left',
        sortable: true
      },
      {
        id: 'customerName',
        header: 'Customer',
        accessor: invoice => (
          <div className="break-words text-sbase text-primaryText line-clamp-1" title={invoice.customerName || '-'}>
            {getStringFiller(invoice.customerName)}
          </div>
        ),
        width: 'w-[200px]',
        sortable: true
      },
      {
        id: 'status',
        header: 'Status',
        accessor: invoice =>
          getStatusBadge({
            status: invoice.status,
            dueDate: invoice.dueDate,
            paidAt: invoice.lastSuccessfulTransactionAt,
            refundedAt: invoice.lastSuccessfulRefundAt,
            closedAt: invoice.closedAt
          }),
        width: 'w-[203px]',
        sortable: true
      },
      {
        id: 'payableAmount',
        header: 'Amount',
        accessor: invoice => (
          <div className="truncate text-sbase text-primaryText">
            {invoice.payableAmount ? getAmountWithCurrency(parseFloat(invoice.payableAmount)) : '-'}
          </div>
        ),
        width: 'w-[150px]',
        sortable: true
      },
      {
        id: 'pendingAmount',
        header: 'Amount Remaining',
        accessor: invoice => (
          <div className="truncate text-sbase text-primaryText">
            {invoice.pendingAmount ? getAmountWithCurrency(parseFloat(invoice.pendingAmount)) : '-'}
          </div>
        ),
        width: 'w-[170px]',
        sortable: true
      },
      {
        id: 'totalPaidAmount',
        header: 'Paid amount',
        accessor: invoice => (
          <div className="truncate text-sbase text-primaryText">
            {invoice.totalPaidAmount ? getAmountWithCurrency(parseFloat(invoice.totalPaidAmount)) : '-'}
          </div>
        ),
        width: 'w-[170px]',
        sortable: true
      },
      {
        id: 'dueDate',
        header: 'Due Date',
        accessor: invoice => <div className="truncate text-sbase text-primaryText">{formatDate(invoice.dueDate)}</div>,
        width: 'w-[150px]',
        sortable: true
      },
      {
        id: 'customerPhone',
        header: 'Customer phone no.',
        accessor: invoice => (
          <div className="truncate text-sbase text-primaryText">{getStringFiller(invoice.customerPhone)}</div>
        ),
        width: 'w-[180px]',
        sortable: true
      },
      {
        id: 'customerEmail',
        header: 'Customer email',
        accessor: invoice => (
          <div className="truncate text-sbase text-primaryText">{getStringFiller(invoice.customerEmail)}</div>
        ),
        width: 'w-[200px]',
        sortable: true
      },
      {
        id: 'merchantName',
        header: 'Created by',
        accessor: invoice => (
          <div className="flex items-center space-x-2">
            <CustomAvatar
              src={invoice.merchantProfilePicureUrl}
              label={getInitials(invoice.merchantName || '')}
              height="h-6"
              width="w-6"
              className="rounded-full"
              fontStyle="text-[8px]"
            />
            <span className="text-sbase text-primaryText">{invoice.merchantName || '-'}</span>
          </div>
        ),
        width: 'w-[200px]',
        sortable: true
      },
      {
        id: 'overdueDate',
        header: 'Overdue date',
        accessor: invoice => {
          if (invoice.status === 'OVERDUE' && invoice.dueDate) {
            return <div className="truncate text-sbase text-primaryText">{formatDate(invoice.dueDate)}</div>;
          }
          return <div className="truncate text-sbase text-primaryText">-</div>;
        },
        width: 'w-[150px]',
        sortable: true
      },
      {
        id: 'paymentMethodList',
        header: 'Payment method used',
        accessor: invoice => (
          <PaymentMethodsCell
            paymentMethodsList={invoice.paymentMethodList}
            totalPayments={invoice.successfulPaymentsCount}
          />
        ),
        width: 'w-[200px]'
      },
      {
        id: 'refundStatus',
        header: 'Refund status',
        accessor: invoice => getDisputeRefundStatusBadge(invoice.refundStatus as DISPUTE_REFUND_STATUS_INDICATOR),
        width: 'w-[150px]'
      },
      {
        id: 'disputeStatus',
        header: 'Dispute status',
        accessor: invoice => getDisputeRefundStatusBadge(invoice.disputeStatus as DISPUTE_REFUND_STATUS_INDICATOR),
        width: 'w-[150px]'
      },
      {
        id: 'estimateStatus',
        header: 'Estimate status',
        accessor: invoice => getEstimateStatusBadge(invoice.estimateStatus),
        width: 'w-[150px]'
      },
      {
        id: 'jobNumber',
        header: 'Job ID',
        accessor: invoice => (
          <div className="truncate text-sbase text-primaryText">
            {invoice.jobId ? (
              <span
                className="cursor-pointer text-secondary"
                onClick={e => {
                  e.stopPropagation();
                  handleJobClick(invoice.jobId);
                }}>
                {invoice.jobNumber}
              </span>
            ) : (
              '-'
            )}
          </div>
        ),
        width: 'w-[150px]'
      },
      {
        id: 'jobStatus',
        header: 'Job status',
        accessor: invoice => getJobStatusBadge(invoice.jobStatus),
        width: 'w-[150px]'
      },
      {
        id: 'estimateNumber',
        header: 'Estimate ID',
        accessor: invoice => (
          <div className="truncate text-sbase text-primaryText">
            {invoice.estimateId ? (
              <span className="cursor-pointer text-secondary" onClick={e => e.stopPropagation()}>
                {invoice.estimateNumber ? `EST-${invoice.estimateNumber}` : invoice.estimateId}
              </span>
            ) : (
              '-'
            )}
          </div>
        ),
        width: 'w-[150px]'
      },
      {
        id: 'totalTipCollected',
        header: 'Tip collected',
        accessor: invoice => (
          <div className="truncate text-sbase text-primaryText">
            {invoice.totalTipCollected ? getAmountWithCurrency(parseFloat(invoice.totalTipCollected)) : '-'}
          </div>
        ),
        width: 'w-[150px]',
        sortable: true
      },
      {
        id: 'actions',
        header: '',
        accessor: invoice => (
          <InvoiceRowActions
            invoiceId={invoice.invoiceId}
            invoiceStatusIndicator={invoice['statusIndicator'] || ''}
            isMarkAsPaidEnabled={isMarkAsPaidEnabled}
            invoiceStatus={invoice.status as InvoiceStatus}
          />
        ),
        fixed: 'right',
        width: 'w-[100px]'
      }
    ];

    return baseColumns;
  }, [currentTeam?.id, isMarkAsPaidEnabled, columnInfo]);

  const textColumns = ['invoiceNumber', 'customerName', 'jobStatus', 'customerEmail', 'merchantName', 'status'];

  return (
    <div className="w-full">
      <GenericTable
        data={invoicesList}
        columns={columns}
        onRowClick={invoice => onInvoiceClick(invoice.invoiceId)}
        className="w-full"
        enableRowHover={true}
        textColumns={textColumns}
        columnInfo={columnInfo}
        highlightedRowId={invoiceIdFromUrl}
        selection={{
          getItemId: invoice => invoice.invoiceId
        }}
        sorting={{
          sortBy: currentSortBy,
          sortOrder: currentSortOrder,
          enableMultiSort: false,
          onSortChange: (sortBy: string, sortOrder: string) => {
            onSortChange?.(sortBy, sortOrder);
          }
        }}
        stickyHorizontalScrollbar={stickyHorizontalScrollbar}
      />
    </div>
  );
};

export default InvoicesTableV2;
